import { useEffect, useRef, useState } from "react";
import "intersection-observer";
import classNames from "classnames";
import { cdnImage } from "../utils/image.utils";
import "./RemoteImage.css";

export type RemoteImageProps = {
    src: string | null | undefined;
    instantLoad?: boolean;
    imageClassName?: string;
    alt?: string;
    wrapperClassName?: string;
    /**
     * Callback fired when the image becomes visible in the viewport.
     * Used by ImageCollection for preloading subsequent images.
     */
    onBecameVisible?: () => void;
};

type Size = {
    width: number;
    height: number;
    auto?: boolean;
};

type SizesProps = {
    sm: Size;
    lg: Size;
    name: string;
};

type SizedImages = {
    smallImage: string;
    largeImage: string;
    smallImageAspectRatio: number;
    largeImageAspectRatio: number;
};

function imageForViewport(images: SizedImages) {
    if (window.matchMedia("(max-width: 720px)").matches) {
        return images.smallImage;
    }

    return images.largeImage;
}

function sizeTheImages(src: string, sizes: SizesProps): SizedImages {
    const { sm, lg } = sizes;

    if (sm.auto || lg.auto) {
        return {
            smallImage: src,
            largeImage: src,
            smallImageAspectRatio: 1,
            largeImageAspectRatio: 1,
        };
    }

    const smallImage = cdnImage(src, sm);
    const largeImage = cdnImage(src, lg);
    const smallImageAspectRatio = sm.width / sm.height;
    const largeImageAspectRatio = lg.width / lg.height;

    return {
        smallImage,
        smallImageAspectRatio,
        largeImage,
        largeImageAspectRatio,
    };
}

function BaseRemoteImage(sizes: SizesProps) {
    const { name } = sizes;

    return function RemoteImage(props: RemoteImageProps) {
        const {
            src,
            instantLoad = false,
            wrapperClassName,
            alt,
            imageClassName,
            onBecameVisible,
        } = props;
        const [isLoading, setIsLoading] = useState(true);
        const [isError, setIsError] = useState(false);
        const [images, setImages] = useState<SizedImages | undefined>(undefined);

        const imageRef = useRef<HTMLImageElement | null>(null);
        // Storing the callback in a ref to avoid re-creating it on every render
        // as it would be added to the useEffect dependency array.
        const onBecameVisibleRef = useRef(onBecameVisible);
        onBecameVisibleRef.current = onBecameVisible;

        useEffect(() => {
            if (src === null || src === undefined) {
                setIsError(true);
                return;
            }
            const imageElement = imageRef.current;

            if (instantLoad) {
                if (imageElement) {
                    imageElement.src = src;
                }

                if (onBecameVisibleRef.current && imageElement) {
                    const visibilityObserver = new IntersectionObserver(
                        (entries) => {
                            entries.forEach((entry) => {
                                if (entry.isIntersecting) {
                                    onBecameVisibleRef.current?.();
                                    visibilityObserver.unobserve(entry.target);
                                }
                            });
                        },
                        {
                            rootMargin: "100px",
                            threshold: 0.1,
                        },
                    );

                    visibilityObserver.observe(imageElement);

                    return () => {
                        visibilityObserver.unobserve(imageElement);
                    };
                }

                return;
            }

            if (!imageElement) return;

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && imageElement) {
                            onBecameVisibleRef.current?.();
                            const sizedImages = sizeTheImages(src, sizes);
                            setImages(sizedImages);
                            imageElement.src = imageForViewport(sizedImages);
                            observer.unobserve(entry.target);
                        }
                    });
                },
                {
                    rootMargin: "100px",
                    threshold: 0.1,
                },
            );

            observer.observe(imageElement);

            return () => {
                if (imageElement) {
                    observer.unobserve(imageElement);
                }
            };
        }, [src, instantLoad]);

        function handleImageLoad() {
            setIsLoading(false);
        }

        function handleImageError() {
            setIsError(true);
            setIsLoading(false);
        }

        const wrapperClass = classNames(
            `web-lib__remote-image web-lib__remote-image__${name}`,
            {
                "web-lib__remote-image--loaded": !isLoading,
                "web-lib__remote-image--error": isError,
                "web-lib__remote-image__skeleton": isLoading && !isError,
            },
            "shrink-0",
        );

        const imageClass = classNames(
            `web-lib__remote-image web-lib__remote-image__${name}`,
            {
                "web-lib__remote-image--loaded": !isLoading,
            },
        );

        return (
            <picture className={classNames(wrapperClass, wrapperClassName)}>
                <source media="(max-width: 720px)" srcSet={images?.smallImage} />
                <source media="(min-width: 720px)" srcSet={images?.largeImage} />

                <img
                    ref={imageRef}
                    alt={alt}
                    data-src={src}
                    className={classNames(imageClass, imageClassName)}
                    style={{
                        visibility: isLoading ? "hidden" : "visible",
                        display: isError ? "none" : "block",
                    }}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                />
            </picture>
        );
    };
}

// Feel free to add new variants if needed,
// also remember to add appropriate CSS class in CSS file RemoteImage.css

const RemoteImage = {
    Card: {
        Tall: BaseRemoteImage({
            sm: { width: 332, height: 498 },
            lg: { width: 344, height: 516 },
            name: "card--tall",
        }),

        Wide: BaseRemoteImage({
            sm: { width: 343, height: 257 },
            lg: { width: 423, height: 317 },
            name: "card--wide",
        }),
    },
    Square: {
        XXSmall: BaseRemoteImage({
            sm: { width: 68, height: 68 },
            lg: { width: 84, height: 84 },
            name: "square--xxs",
        }),

        XSmall: BaseRemoteImage({
            sm: { width: 80, height: 80 },
            lg: { width: 96, height: 96 },
            name: "square--xs",
        }),

        Small: BaseRemoteImage({
            sm: { width: 96, height: 96 },
            lg: { width: 120, height: 120 },
            name: "square--sm",
        }),

        Medium: BaseRemoteImage({
            sm: { width: 148, height: 148 },
            lg: { width: 180, height: 180 },
            name: "square--md",
        }),

        Large: BaseRemoteImage({
            sm: { width: 265, height: 265 },
            lg: { width: 280, height: 280 },
            name: "square--lg",
        }),
    },

    Rounded: {
        XXSmall: BaseRemoteImage({
            sm: { width: 48, height: 48 },
            lg: { width: 64, height: 64 },
            name: "rounded--xxs",
        }),

        XSmall: BaseRemoteImage({
            sm: { width: 64, height: 64 },
            lg: { width: 80, height: 80 },
            name: "rounded--xs",
        }),

        Small: BaseRemoteImage({
            sm: { width: 80, height: 80 },
            lg: { width: 120, height: 120 },
            name: "rounded--sm",
        }),

        Medium: BaseRemoteImage({
            sm: { width: 148, height: 148 },
            lg: { width: 180, height: 180 },
            name: "rounded--md",
        }),

        Large: BaseRemoteImage({
            sm: { width: 265, height: 265 },
            lg: { width: 280, height: 280 },
            name: "rounded--lg",
        }),
    },
    Fluid: BaseRemoteImage({
        sm: { width: 375, height: 500 },
        lg: { width: 1120, height: 630 },
        name: "fluid",
    }),
    Auto: BaseRemoteImage({
        sm: { width: 0, height: 0, auto: true },
        lg: { width: 0, height: 0, auto: true },
        name: "auto",
    }),
};

export default RemoteImage;
